import React from 'react';
import { AuditLogDto, SEVERITY_LABELS } from '../../../types/audit';
import { Dialog } from '../../../components/Dialog';

interface Props {
  log: AuditLogDto | null;
  onClose: () => void;
}

export function AuditLogDetailsModal({ log, onClose }: Props) {
  if (!log) return null;

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleString('es-ES', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
    });
  };

  const getSeverityColorClasses = (severity: string) => {
    const colorMap: Record<string, string> = {
      Info: 'bg-blue-100 text-blue-800',
      Warning: 'bg-yellow-100 text-yellow-800',
      Error: 'bg-orange-100 text-orange-800',
      Critical: 'bg-red-100 text-red-800',
    };
    return colorMap[severity] || 'bg-gray-100 text-gray-800';
  };

  return (
    <Dialog
      isOpen={!!log}
      onClose={onClose}
      title="Detalles del Log"
      size="xl"
    >
      <div className="space-y-6">
        {/* Información básica */}
        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="font-semibold text-gray-700">ID:</label>
            <p className="text-sm text-gray-600 font-mono break-all">{log.id}</p>
          </div>
          <div>
            <label className="font-semibold text-gray-700">Fecha/Hora:</label>
            <p className="text-sm text-gray-600">{formatDate(log.timestamp)}</p>
          </div>
        </div>

        {/* Usuario */}
        {(log.username || log.clientId) && (
          <div className="grid grid-cols-2 gap-4 bg-gray-50 p-4 rounded-lg">
            <div>
              <label className="font-semibold text-gray-700">Usuario:</label>
              <p className="text-sm text-gray-600">{log.username || log.clientId || '-'}</p>
            </div>
            {log.userRole && (
              <div>
                <label className="font-semibold text-gray-700">Rol:</label>
                <p className="text-sm text-gray-600">{log.userRole}</p>
              </div>
            )}
            {log.clientType && (
              <div>
                <label className="font-semibold text-gray-700">Tipo de Cliente:</label>
                <p className="text-sm text-gray-600">{log.clientType}</p>
              </div>
            )}
          </div>
        )}

        {/* Acción */}
        <div className="grid grid-cols-3 gap-4 bg-gray-50 p-4 rounded-lg">
          <div>
            <label className="font-semibold text-gray-700">Módulo:</label>
            <p className="text-sm text-gray-600">{log.module}</p>
          </div>
          <div>
            <label className="font-semibold text-gray-700">Acción:</label>
            <p className="text-sm text-gray-600">{log.action}</p>
          </div>
          <div>
            <label className="font-semibold text-gray-700">Severidad:</label>
            <span className={`inline-block px-3 py-1 text-xs font-semibold rounded-full ${getSeverityColorClasses(log.severity)}`}>
              {SEVERITY_LABELS[log.severity] || log.severity}
            </span>
          </div>
        </div>

        {/* Entidad */}
        {log.entityType && (
          <div className="grid grid-cols-2 gap-4 bg-gray-50 p-4 rounded-lg">
            <div>
              <label className="font-semibold text-gray-700">Tipo de Entidad:</label>
              <p className="text-sm text-gray-600">{log.entityType}</p>
            </div>
            <div>
              <label className="font-semibold text-gray-700">ID de Entidad:</label>
              <p className="text-sm text-gray-600 font-mono break-all">{log.entityId}</p>
            </div>
          </div>
        )}

        {/* Descripción */}
        {log.description && (
          <div className="bg-gray-50 p-4 rounded-lg">
            <label className="font-semibold text-gray-700">Descripción:</label>
            <p className="text-sm text-gray-600 mt-2">{log.description}</p>
          </div>
        )}

        {/* Valores antiguos/nuevos */}
        {(log.oldValues || log.newValues) && (
          <div className="border-t pt-4">
            <h3 className="font-semibold text-gray-800 mb-3">Cambios Realizados</h3>
            <div className="grid grid-cols-2 gap-4">
              {log.oldValues && (
                <div>
                  <label className="font-semibold text-gray-700 block mb-2">Valores Antiguos:</label>
                  <pre className="text-xs bg-red-50 border border-red-200 p-3 rounded-lg overflow-x-auto max-h-64">
                    {JSON.stringify(log.oldValues, null, 2)}
                  </pre>
                </div>
              )}
              {log.newValues && (
                <div>
                  <label className="font-semibold text-gray-700 block mb-2">Valores Nuevos:</label>
                  <pre className="text-xs bg-green-50 border border-green-200 p-3 rounded-lg overflow-x-auto max-h-64">
                    {JSON.stringify(log.newValues, null, 2)}
                  </pre>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Contexto HTTP */}
        <div className="border-t pt-4">
          <h3 className="font-semibold text-gray-800 mb-3">Contexto HTTP</h3>
          <div className="grid grid-cols-2 gap-4 text-sm bg-gray-50 p-4 rounded-lg">
            <div>
              <label className="font-medium text-gray-700">Dirección IP:</label>
              <p className="text-gray-600">{log.ipAddress || '-'}</p>
            </div>
            <div>
              <label className="font-medium text-gray-700">Método:</label>
              <p className="text-gray-600">
                {log.requestMethod ? (
                  <span className="font-mono bg-gray-200 px-2 py-1 rounded">{log.requestMethod}</span>
                ) : '-'}
              </p>
            </div>
            <div className="col-span-2">
              <label className="font-medium text-gray-700">Ruta:</label>
              <p className="text-gray-600 font-mono text-xs break-all">{log.requestPath || '-'}</p>
            </div>
            <div className="col-span-2">
              <label className="font-medium text-gray-700">User Agent:</label>
              <p className="text-gray-600 text-xs break-all">{log.userAgent || '-'}</p>
            </div>
          </div>
        </div>

        {/* Estado */}
        <div className="bg-gray-50 p-4 rounded-lg">
          <label className="font-semibold text-gray-700">Estado de la Operación:</label>
          <p className="text-sm mt-2">
            {log.isSuccess ? (
              <span className="text-green-600 font-semibold">✓ Exitosa</span>
            ) : (
              <span className="text-red-600 font-semibold">✗ Fallida</span>
            )}
          </p>
        </div>

        {/* Footer */}
        <div className="flex justify-end border-t pt-4">
          <button
            onClick={onClose}
            className="px-6 py-2 bg-gray-200 text-gray-700 rounded-md hover:bg-gray-300 transition-colors font-medium"
          >
            Cerrar
          </button>
        </div>
      </div>
    </Dialog>
  );
}
